import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useCreateMcpServerConnection, useUpdateMcpServerConnection } from '@/hooks/useMcpServerConnectionQueries';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { McpServerConnectionSafe } from '@vivport/backend/types';
import { useState } from 'react';

type McpConnectionFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: McpServerConnectionSafe | null;
  onCreated?: (created: McpServerConnectionSafe) => void;
};

type McpConnectionFormProps = {
  connection: McpServerConnectionSafe | null;
  onClose: () => void;
  onCreated?: (created: McpServerConnectionSafe) => void;
};

function McpConnectionForm({ connection, onClose, onCreated }: McpConnectionFormProps) {
  const createMutation = useCreateMcpServerConnection();
  const updateMutation = useUpdateMcpServerConnection();
  const [name, setName] = useState(connection?.name ?? '');
  const [url, setUrl] = useState(connection?.url ?? '');
  const [authHeaderName, setAuthHeaderName] = useState(connection?.authHeaderName ?? '');
  const [authHeaderValue, setAuthHeaderValue] = useState('');
  const [enabled, setEnabled] = useState(connection?.enabled ?? true);
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = () => {
    setFormError(null);
    createMutation.mutate(
      {
        name: name.trim(),
        url: url.trim(),
        ...(authHeaderName.trim().length > 0 ? { authHeaderName: authHeaderName.trim() } : {}),
        ...(authHeaderValue.length > 0 ? { authHeaderValue } : {}),
        enabled,
      },
      {
        onSuccess: (created) => {
          onCreated?.(created);
          onClose();
        },
        onError: (error) => setFormError(getAxiosErrorDisplayMessage(error, 'Failed to create connection.')),
      }
    );
  };

  const handleUpdate = (target: McpServerConnectionSafe) => {
    setFormError(null);
    updateMutation.mutate(
      {
        id: target.id,
        input: {
          name: name.trim(),
          url: url.trim(),
          enabled,
          authHeaderName: authHeaderName.trim().length > 0 ? authHeaderName.trim() : null,
          // Empty password field leaves the stored secret unchanged; clearing the header
          // name above (null) is the explicit "remove auth" path.
          ...(authHeaderValue.length > 0 ? { authHeaderValue } : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (error) => setFormError(getAxiosErrorDisplayMessage(error, 'Failed to update connection.')),
      }
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{connection ? 'Edit MCP server connection' : 'Add MCP server connection'}</DialogTitle>
        <DialogDescription>
          {connection
            ? 'Leave the auth header value blank to keep the stored secret unchanged.'
            : 'The auth header value is stored encrypted and never displayed again.'}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-name">Name</Label>
            <Input
              id="mcp-name"
              placeholder="my-tool-server"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-url">URL</Label>
            <Input
              id="mcp-url"
              type="url"
              placeholder="https://…"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-auth-name">Auth header name (optional)</Label>
            <Input
              id="mcp-auth-name"
              placeholder="Authorization"
              value={authHeaderName}
              onChange={(event) => setAuthHeaderName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-auth-value">
              Auth header value{connection ? ' (leave blank to keep current)' : ''}
            </Label>
            <Input
              id="mcp-auth-value"
              type="password"
              autoComplete="off"
              placeholder={connection && connection.authHeaderName ? '••••••••' : ''}
              value={authHeaderValue}
              onChange={(event) => setAuthHeaderValue(event.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="mcp-enabled">Enabled</Label>
            <Switch id="mcp-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {connection ? (
          <Button onClick={() => handleUpdate(connection)} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

export function McpConnectionFormDialog({ open, onOpenChange, connection, onCreated }: McpConnectionFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <McpConnectionForm connection={connection} onClose={() => onOpenChange(false)} onCreated={onCreated} />
      </DialogContent>
    </Dialog>
  );
}
